var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import { customConverter } from '../../util/custom-converter';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import '../button/button';
import '../icon/icons/close';
import styles from './toast.styles';
import { SlotController } from '../../controllers/slot-controller';
/**
 * @slot default - The component content
 * @slot before - Before icon slot
 *
 * @fires bl-toast-open - Emitted when a toast opens
 * @fires bl-toast-close - Emitted when a toast close
 * @fires bl-toast-cancel - Emitted before the toast has closed when a user attempts to dismiss a toast. Call preventDefault() on the event to prevent the toast closing.
 * @csspart base - The component's base wrapper.
 * @csspart icon - The component's icon wrapper.
 * @csspart part-before - The component's before slot.
 * @csspart body - The component's body wrapper.
 * @csspart part-default - The component's default slot.
 * @csspart close-button - The component's close-button element.
 * @csspart close-button-icon - The component's close-button icon.

 */
export class BlocksToast extends BlocksElement {
    constructor() {
        super(...arguments);
        /**
         * Close button text
         */
        this.closeButtonText = 'Close';
        /**
         * Delay property
         * 1) Number of seconds to close the toast
         * 2) Default amount is 3. This is **deprecated** and the default value will be changed to 6 seconds.
         * @deprecated
         */
        this.delay = 3; /* 2 */
        /**
         * Auto close property
         * 1) Add the ability to auto close the toast after the 3 seconds by default, need to be false if not needed this property.
          Web components won't support Boolean properties when their default value is set to true. It will always be true
          irrespective of whether it is set to false from consuming side. This custom converter helps teams using vanilla js (as it
          will be set as attribute for them (as string)) and this custom converter will convert it to corresponding boolean value
         */
        this.autoClose = true;
        /**
         * Dismissible property
         * 1) Adds the ability to close when toggled to true and hide when false
         */
        this.dismissible = true;
        /**
         * Position option
         * 1) Display the toast at the bottom if true
         */
        this.isBottom = false;
        /**
         * @default true , controls whether focus should return to the trigger element once the toast closes
         */
        this.returnFocusToTrigger = true;
        /**
         * The ARIA live region politeness level for the loader.
         *
         * @type {string}
         * @default "assertive"
         */
        this.arialive = 'assertive';
        this.showAnimation = false;
        this.slots = new SlotController(this, null, 'before');
    }
    static { this.styles = styles; }
    /**
     * Get the trigger element
     */
    findActiveElement() {
        if (!this.closest('cat-toast-group')) {
            this.previouslyActiveElement = document.activeElement;
        }
    }
    /**
     * Updated lifecycle
     */
    updated(changedProperties) {
        if (changedProperties.has('autoClose')) {
            this.handleAutoClose();
        }
        if (changedProperties.has('isDismissed') && !this.isDismissed) {
            this.dispatch({ eventName: 'bl-toast-open', detailObj: { isActive: true } });
            this.showAnimation = false;
            this.findActiveElement();
        }
    }
    /**
     * Disconnected callback lifecycle
     */
    disconnectedCallback() {
        super.disconnectedCallback();
        clearTimeout(this._timer);
    }
    /**
     * On toast close
     * 1) Function that sets isDismissed to true and removes the toast from the UI
      after 1 second and sets the focus back to trigger if any.
     */
    close() {
        if (this.emitCancelEvent()) {
            return;
        }
        this.showAnimation = true;
        setTimeout(() => {
            this.isDismissed = true; /* 1 */
            if (this.returnFocusToTrigger && this.previouslyActiveElement) {
                this.previouslyActiveElement.focus();
            }
            this.dispatch({ eventName: 'bl-toast-close', detailObj: { isActive: false } });
        }, 1000);
    }
    /**
     * Auto close
     * 1) If auto close is true and modal is not closed dismiss after delay time.
     */
    handleAutoClose() {
        if (this.autoClose) {
            /**1 */
            this._timer = setTimeout(() => {
                if (!this.isDismissed && this.autoClose) {
                    this.close();
                }
                clearTimeout(this._timer);
            }, this.delay * 1000);
        }
    }
    /**
     * Mouseover event
     * 1) On mouseover of the toast, clear the timer.
     */
    handleMouseOver() {
        clearTimeout(this._timer); /* 1 */
    }
    /**
     * Mouseleave event
     * 1) On mouseleave of the toast, reset the timeout.
     * 2) On mouseleave of the toast, reset the timer.
     */
    handleMouseLeave() {
        clearTimeout(this._timer); /* 1 */
        this.handleAutoClose(); /* 2 */
    }
    /**
     * Keydown event
     * 1) If escape key is struck when the toast is active, dismiss it
     */
    handleKeyDown(e) {
        if (!this.isDismissed) {
            if (e.key === KEYS.ESCAPE) {
                this.close(); /* 1 */
            }
        }
    }
    /**
     * @fires cancel If we call preventDefault on cancel event object to not close toaster.
     * @returns boolean
     */
    emitCancelEvent() {
        const cancelEvent = this.dispatch({
            eventName: 'bl-toast-cancel',
            detailObj: {},
            optionsObj: { cancelable: true }
        });
        return cancelEvent.defaultPrevented;
    }
    render() {
        const componentClassNames = this.componentClassNames('cat-c-toast', {
            'cat-c-toast--success': this.variant === 'success',
            'cat-c-toast--warning': this.variant === 'warning',
            'cat-c-toast--error': this.variant === 'error',
            'cat-c-toast--bottom': this.isBottom === true,
            'cat-is-dismissible': this.dismissible === true,
            'cat-has-animation': this.showAnimation === true,
            'cat-c-toast--has-before': this.slots.hasSlotted('before')
        });
        return !this.isDismissed
            ? html `
          <div
            part="base"
            role="alert"
            aria-live=${this.arialive}
            class="${componentClassNames}"
            @mouseover="${this.handleMouseOver}"
            @mouseleave="${this.handleMouseLeave}"
            @keydown=${this.handleKeyDown}
          >
            <div part="icon" class="cat-c-toast__icon">
              <slot part="part-before" name="before"></slot>
            </div>
            <div part="body" class="cat-c-toast__body">
              <slot part="part-default"></slot>
            </div>

            ${this.dismissible
                ? html `
                  <cat-button
                    part="close-button"
                    exportparts=${this.getExportParts('button')}
                    class="cat-c-toast__close-button"
                    @click=${this.close}
                    variant="ghost"
                    ?hidetext=${true}
                  >
                    ${this.closeButtonText}
                    <cat-icon-close
                      part="close-button-icon"
                      exportparts=${this.getExportParts('close', 'icon')}
                      slot="after"
                      size="sm"
                    ></cat-icon-close>
                  </cat-button>
                `
                : nothing}
          </div>
        `
            : html `${nothing}`;
    }
}
__decorate([
    property({ type: String })
], BlocksToast.prototype, "closeButtonText", void 0);
__decorate([
    property({ type: Number })
], BlocksToast.prototype, "delay", void 0);
__decorate([
    property({
        // Allow "true|false" attribute values but keep the property boolean
        type: Boolean,
        converter: customConverter()
    })
], BlocksToast.prototype, "autoClose", void 0);
__decorate([
    property({ type: Boolean })
], BlocksToast.prototype, "isDismissed", void 0);
__decorate([
    property({ type: Boolean })
], BlocksToast.prototype, "dismissible", void 0);
__decorate([
    property({ type: String })
], BlocksToast.prototype, "variant", void 0);
__decorate([
    property({ type: Boolean })
], BlocksToast.prototype, "isBottom", void 0);
__decorate([
    property({
        // Allow "true|false" attribute values but keep the property boolean
        type: Boolean,
        converter: customConverter()
    })
], BlocksToast.prototype, "returnFocusToTrigger", void 0);
__decorate([
    property({ type: String })
], BlocksToast.prototype, "arialive", void 0);
__decorate([
    state()
], BlocksToast.prototype, "showAnimation", void 0);
if (customElements.get('cat-toast') === undefined) {
    customElements.define('cat-toast', BlocksToast);
}
